import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import downArrow from "../../assets/down-arrow-icon.png";

const translations = {
  en: {
    jobFitScore: "Job Fit Score",
    careerFitInsights: "Career Fit Insights",
    potentialRiskAreas: "Potential Risk Areas",
    personalStrengths: "Personal Strengths",
    developmentAreas: "Development Areas",
    strengthsDescription:
      "Your personality profile indicates these key strengths that can be leveraged in your career path. Focus on roles that allow you to utilize these natural abilities.",
    careerDomainFit: "Career Domain Fit",
    careerDomainSuggestions: "Career Domain Suggestions",
    basedOnProfile: "Based on",
    profile: "profile",
    predictiveValidity: "Predictive Validity",
    careerDomainsDescription:
      "These career domains align with your personality traits and interests. Consider exploring roles within these fields that match your skills and experience.",
  },
  nl: {
    jobFitScore: "Functiegeschiktheidsscore",
    careerFitInsights: "Carrière Inzichten",
    potentialRiskAreas: "Mogelijke Risicogebieden",
    personalStrengths: "Persoonlijke Sterke Punten",
    developmentAreas: "Ontwikkelingsgebieden",
    strengthsDescription:
      "Uw persoonlijkheidsprofiel geeft aan dat deze sterke punten kunnen worden benut in uw carrièrepad. Focus op rollen die u in staat stellen deze natuurlijke vaardigheden te gebruiken.",
    careerDomainFit: "Carrière Domein Geschiktheid",
    careerDomainSuggestions: "Carrière Domein Suggesties",
    basedOnProfile: "Gebaseerd op",
    profile: "profiel",
    predictiveValidity: "Voorspellende Validiteit",
    careerDomainsDescription:
      "Deze carrièredomeinen sluiten aan bij uw persoonlijkheidstrekken en interesses. Overweeg om rollen binnen deze vakgebieden te verkennen die aansluiten bij uw vaardigheden en ervaring.",
  },
};

const AdvancedAnalysis = ({
  analysisResults,
  isRecruiterMode,
  isGeneratingPDF,
  isDutch = false,
}) => {
  const [isPredictiveValidityOpen, setIsPredictiveValidityOpen] =
    useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [lang, setLang] = useState(isDutch ? "nl" : "en");
  const t = translations[lang];

  const jobFitScore =
    analysisResults?.recommendations.jobFitAnalysis?.overallScore || 0;
  const jobFitInterpretation =
    analysisResults?.recommendations.jobFitAnalysis?.interpretation;
  const suggestedCareers = analysisResults?.riasec?.suggestedCareers || [
    "No suggested careers",
  ];
  const strengthAreas =
    analysisResults?.recommendations?.jobFitAnalysis?.strengthAreas || [];
  const developmentAreas =
    analysisResults?.recommendations?.jobFitAnalysis?.developmentAreas || [];
  const riskAreas =
    analysisResults?.recommendations?.jobFitAnalysis?.riskAreas || "";

  const validityData = analysisResults?.recommendations.predictiveValidity || {
    ffm: { level: "High", primaryUse: "predicts performance" },
    riasec: { level: "Moderate", primaryUse: "predicts career fit" },
    mbti: { level: "Low", primaryUse: "use for team dynamics only" },
  };

  useEffect(() => {
    const checkLanguage = () => {
      const cookieIsDutch = Cookies.get("isDutch") === "true";
      const newLang = cookieIsDutch ? "nl" : "en";
      if (newLang !== lang) {
        setLang(newLang);
      }
    };

    // Check immediately
    checkLanguage();

    // Set up an interval to check for changes
    const interval = setInterval(checkLanguage, 1000);

    return () => clearInterval(interval);
  }, [lang]);

  useEffect(() => {
    if (isRecruiterMode) {
      setAnimatedScore(0);
      const duration = 1000;
      const steps = 15;
      const increment = jobFitScore / steps;
      const stepDuration = duration / steps;

      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= jobFitScore) {
          setAnimatedScore(jobFitScore);
          clearInterval(timer);
        } else {
          setAnimatedScore(Math.round(current));
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [isRecruiterMode, jobFitScore]);

  return (
    <div className="flex gap-4 animate-text">
      <div className="flex-1 flex flex-col">
        <h2 className="text-xl font-bold text-primary">
          {isRecruiterMode ? t.jobFitScore : t.careerFitInsights}
        </h2>
        <div className="border p-4 rounded-md bg-[#121212] flex mt-2 flex-1">
          <div className="flex flex-col justify-between w-full">
            {isRecruiterMode ? (
              <>
                <p
                  className={`text-9xl font-semibold mt-neg-20 w-fit ${
                    !isGeneratingPDF ? "gradient-text" : "text-logoYellow"
                  }`}
                >
                  {animatedScore}%
                </p>
                <p className="text-sm text-gray-200 mt-2">
                  {jobFitInterpretation}
                </p>

                {riskAreas && (
                  <div className="mt-8">
                    <h3 className=" font-bold text-red-400">
                      {t.potentialRiskAreas}
                    </h3>
                    <p className="text-sm text-gray-200 mt-2">{riskAreas}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <h3 className=" font-bold text-primary">
                    {t.personalStrengths}
                  </h3>
                  <div className="text-sm flex flex-wrap gap-2 mt-2">
                    {strengthAreas.map((area, i) => (
                      <span
                        key={i}
                        className="p-2 rounded-md border !border-white"
                      >
                        <p className="text-white">{area}</p>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className=" font-bold text-primary mt-4">
                    {t.developmentAreas}
                  </h3>
                  <div className="text-sm flex flex-wrap gap-2 mt-2">
                    {developmentAreas.map((area, i) => (
                      <span
                        key={i}
                        className="p-2 rounded-md border !border-white"
                      >
                        <p className="text-white">{area}</p>
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-sm text-gray-200 mt-8">
                  {t.strengthsDescription}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col ">
        <h2 className="text-xl font-bold text-primary">
          {isRecruiterMode ? t.careerDomainFit : t.careerDomainSuggestions}
        </h2>
        <div className="container-text-white border p-4 rounded-md gradient-bg-3 !border-none flex gap-2 flex-col mt-2 flex-1">
          <div>
            <p className="font-bold">
              {t.basedOnProfile} {analysisResults.riasec.primaryType}-
              {analysisResults.riasec.secondaryType} {t.profile}
            </p>
          </div>
          <div className="flex-1">
            <div className="text-sm flex flex-wrap gap-2">
              {suggestedCareers.map((job, i) => (
                <span key={i} className="p-2 rounded-md border">
                  <p>{job}</p>
                </span>
              ))}
            </div>
          </div>

          {isRecruiterMode ? (
            <div className="text-gray-600 mt-4">
              <div
                className="flex justify-between gap-2 py-1 cursor-pointer"
                onClick={() =>
                  setIsPredictiveValidityOpen(!isPredictiveValidityOpen)
                }
              >
                <h3 className="font-bold">{t.predictiveValidity}</h3>
                <img
                  src={downArrow}
                  className={`invert w-4 h-4 ${
                    isPredictiveValidityOpen && "rotate-180"
                  }`}
                />
              </div>
              {isPredictiveValidityOpen && (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    {Object.entries(validityData).map(([model, data]) => (
                      <div key={model} className="w-full">
                        <div>
                          <div className="flex justify-between gap-2">
                            <span className="font-semibold">
                              {model.toUpperCase()}
                            </span>
                            <span className="text-sm">{data.score}%</span>
                          </div>
                          <div className="relative w-full h-2 bg-[#F6AF96] rounded mt-2">
                            <div
                              className="absolute h-full bg-hoverBg rounded"
                              style={{
                                width: `${data.score}%`,
                                backgroundColor: `#fff`,
                              }}
                            />
                          </div>
                          <p className="text-xs mt-2">{data.primaryUse}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-gray-600 mt-4">
              <p className="text-sm">{t.careerDomainsDescription}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalysis;
